// KB Site Config — per-site overrides for a base Knowledge Base.
// A domain may have multiple sites (Chermside, Morningside, Docklands…),
// each needing its own price guide, premium products, recommendations
// and email template on top of the same base KB.
//
// Layout:
//   Domain picker → KB picker → per-site tab strip → editor form.
// Sites are read from `domain.extension_sites` (agent → site map);
// unique site labels populate the tab strip. An extra "Add site" tab
// lets a manager type a new label ad-hoc if a site exists in real life
// but doesn't yet own an extension.
//
// RBAC (enforced server-side by `routes/kb_site_configs.py`):
//   master_admin / domain_admin / office_manager → all sites in tenant
//   domain_supervisor → only sites in their `assigned_sites` scope
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Save, Building2, Globe, BookOpen, Plus, Trash2, X,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_CFG = {
  price_guide: '',
  premium_products: [],
  recommendations: '',
  email_template: { subject: '', body: '' },
};

const AdminKBSiteConfigPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domains, setDomains] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [kbs, setKbs] = useState([]);
  const [selectedKbId, setSelectedKbId] = useState('');
  const [sites, setSites] = useState([]);            // ['Chermside', 'Morningside', …]
  const [selectedSite, setSelectedSite] = useState('');
  const [configs, setConfigs] = useState([]);        // list of saved configs for this KB
  const [form, setForm] = useState(EMPTY_CFG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSiteInput, setNewSiteInput] = useState('');
  const [showAddSite, setShowAddSite] = useState(false);
  const [productInput, setProductInput] = useState('');

  // ── Domains ────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/admin/domains`, { headers })
      .then(r => {
        setDomains(r.data || []);
        if (r.data?.length > 0) setSelectedDomainId(r.data[0].id);
      })
      .catch(() => toast.error('Failed to load domains'));
  }, [headers]);

  // ── KBs + sites for the picked domain ──────────────────────
  useEffect(() => {
    if (!selectedDomainId) return;
    axios.get(`${API}/knowledge-bases`, { headers, params: { domain_id: selectedDomainId } })
      .then(r => {
        const domainKbs = (r.data || []).filter(k => k.domain_id === selectedDomainId);
        setKbs(domainKbs);
        setSelectedKbId(domainKbs[0]?.id || '');
      })
      .catch(() => toast.error('Failed to load knowledge bases'));

    // Derive sites from domain.extension_sites
    const d = domains.find(x => x.id === selectedDomainId);
    const extSites = d?.extension_sites || {};
    const uniqueSites = Array.from(new Set(
      Object.values(extSites).map(s => String(s || '').trim()).filter(Boolean),
    )).sort();
    setSites(uniqueSites);
    setSelectedSite(uniqueSites[0] || '');
  }, [selectedDomainId, headers, domains]);

  // ── Load all configs for picked KB ─────────────────────────
  const loadConfigs = useCallback(async () => {
    if (!selectedKbId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kb/${selectedKbId}/site-configs`, { headers });
      setConfigs(r.data?.configs || []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedKbId, headers]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  // ── When site or configs change, load its form ─────────────
  useEffect(() => {
    if (!selectedSite) { setForm(EMPTY_CFG); return; }
    const existing = configs.find(c => c.site === selectedSite);
    if (existing) {
      setForm({
        price_guide: existing.price_guide || '',
        premium_products: existing.premium_products || [],
        recommendations: existing.recommendations || '',
        email_template: existing.email_template || { subject: '', body: '' },
      });
    } else {
      setForm(EMPTY_CFG);
    }
  }, [selectedSite, configs]);

  // ── Save ───────────────────────────────────────────────────
  const save = async () => {
    if (!selectedKbId || !selectedSite) {
      toast.error('Pick a KB and a site first');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/kb/${selectedKbId}/site-configs/${encodeURIComponent(selectedSite)}`,
        form,
        { headers },
      );
      toast.success(`Saved override for ${selectedSite}`);
      await loadConfigs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!selectedKbId || !selectedSite) return;
    if (!window.confirm(`Delete site override for "${selectedSite}"? Grading will fall back to the base KB.`)) return;
    try {
      await axios.delete(
        `${API}/kb/${selectedKbId}/site-configs/${encodeURIComponent(selectedSite)}`,
        { headers },
      );
      toast.success('Override removed — falling back to base KB');
      await loadConfigs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const addProduct = () => {
    const v = productInput.trim();
    if (!v) return;
    if (form.premium_products.includes(v)) { toast.info('Already added'); return; }
    setForm({ ...form, premium_products: [...form.premium_products, v] });
    setProductInput('');
  };

  const removeProduct = (p) => {
    setForm({ ...form, premium_products: form.premium_products.filter(x => x !== p) });
  };

  const addSiteAdHoc = () => {
    const v = newSiteInput.trim();
    if (!v) return;
    if (!sites.includes(v)) setSites([...sites, v].sort());
    setSelectedSite(v);
    setNewSiteInput('');
    setShowAddSite(false);
  };

  const selectedKb = kbs.find(k => k.id === selectedKbId);
  const configExistsForSite = configs.some(c => c.site === selectedSite);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="kb-site-config-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Site-Specific KB Configuration</h1>
          <p className="text-white/40 text-sm mt-0.5">
            Override the base Knowledge Base per site — different price guides, premium products,
            recommendations, and email templates for Chermside vs Morningside vs Docklands.
          </p>
        </div>

        {/* Domain picker */}
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-white/40 text-xs font-medium uppercase tracking-wide">Domain</p>
          <div className="flex flex-wrap gap-2">
            {domains.map(d => (
              <button
                key={d.id}
                onClick={() => setSelectedDomainId(d.id)}
                data-testid={`kbsc-domain-${d.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedDomainId === d.id
                    ? 'bg-teal-500/20 border border-teal-500/40 text-teal-300'
                    : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
                }`}
              >
                <Building2 className="w-3 h-3" />
                {d.company?.name || d.yabbit_domain}
              </button>
            ))}
          </div>
        </div>

        {/* KB picker */}
        <div className="flex items-start gap-3 flex-wrap">
          <p className="text-white/40 text-xs font-medium uppercase tracking-wide pt-2">KB</p>
          {kbs.length === 0 ? (
            <p className="text-white/40 text-sm pt-1.5">No knowledge bases for this domain.</p>
          ) : (
            <div className="flex flex-wrap gap-2 flex-1">
              {kbs.map(k => (
                <button
                  key={k.id}
                  onClick={() => setSelectedKbId(k.id)}
                  data-testid={`kbsc-kb-${k.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    selectedKbId === k.id
                      ? 'bg-indigo-500/20 border border-indigo-500/40 text-indigo-200'
                      : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
                  }`}
                >
                  <BookOpen className="w-3 h-3" />
                  {k.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Site tabs */}
        {selectedKbId && (
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-white/40 text-xs font-medium uppercase tracking-wide">Sites</p>
              <div className="flex flex-wrap gap-2 flex-1">
                {sites.length === 0 && (
                  <p className="text-white/40 text-xs italic">
                    No sites configured yet. Map extensions to sites on the Domain Overview tab,
                    or add one ad-hoc below.
                  </p>
                )}
                {sites.map(s => {
                  const hasCfg = configs.some(c => c.site === s);
                  return (
                    <button
                      key={s}
                      onClick={() => setSelectedSite(s)}
                      data-testid={`kbsc-site-tab-${s}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        selectedSite === s
                          ? 'bg-amber-500/20 border border-amber-500/40 text-amber-200'
                          : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
                      }`}
                    >
                      <Globe className="w-3 h-3" />
                      {s}
                      {hasCfg && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                    </button>
                  );
                })}
                <button
                  onClick={() => setShowAddSite(v => !v)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-white/5 border border-dashed border-white/20 text-white/50 hover:text-white/80"
                  data-testid="kbsc-add-site-btn"
                >
                  <Plus className="w-3 h-3" />Add site
                </button>
              </div>
            </div>
            {showAddSite && (
              <div className="mt-3 flex items-center gap-2 max-w-sm">
                <input
                  value={newSiteInput}
                  onChange={e => setNewSiteInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addSiteAdHoc()}
                  placeholder="New site label (e.g. Docklands)"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                  data-testid="kbsc-new-site-input"
                />
                <button
                  onClick={addSiteAdHoc}
                  className="px-3 py-1.5 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded-lg text-sm"
                >
                  Add
                </button>
              </div>
            )}
          </div>
        )}

        {/* Editor */}
        {selectedKbId && selectedSite && (
          <div
            className="bg-white/[0.02] border border-white/10 rounded-xl p-6 space-y-5"
            data-testid="kbsc-editor"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white text-lg font-semibold">
                  {selectedKb?.name} <span className="text-white/40">·</span> <span className="text-amber-300">{selectedSite}</span>
                </h2>
                <p className="text-white/40 text-xs mt-0.5">
                  Overrides apply on grading, playbook, and coaching-email generation for calls at this site.
                </p>
              </div>
              {configExistsForSite && (
                <button
                  onClick={clearOverride}
                  className="flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 rounded-lg px-3 py-1.5"
                  data-testid="kbsc-clear-override-btn"
                >
                  <Trash2 className="w-3 h-3" />Delete override
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-white/30 animate-spin" /></div>
            ) : (
              <>
                {/* Price Guide */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-1.5">
                    Price guide
                    <span className="ml-2 text-white/30 text-xs font-normal">shown to agents on calls at this site</span>
                  </label>
                  <textarea
                    value={form.price_guide}
                    onChange={e => setForm({ ...form, price_guide: e.target.value })}
                    rows={4}
                    placeholder="e.g. Standard 3mo = $499, Elite 3mo = $699, Rehab add-on = $99/mo"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                    data-testid="kbsc-price-guide"
                  />
                </div>

                {/* Premium Products */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-1.5">
                    Premium products
                    <span className="ml-2 text-white/30 text-xs font-normal">recommend these first when the caller shows intent</span>
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={productInput}
                      onChange={e => setProductInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                      placeholder="Add a product / plan name…"
                      className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                      data-testid="kbsc-product-input"
                    />
                    <button
                      onClick={addProduct}
                      className="px-3 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded-lg text-sm"
                      data-testid="kbsc-product-add"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.premium_products.map(p => (
                      <span
                        key={p}
                        className="inline-flex items-center gap-1 bg-indigo-500/10 border border-indigo-500/30 text-indigo-200 text-xs rounded-full pl-2.5 pr-1 py-0.5"
                      >
                        {p}
                        <button
                          onClick={() => removeProduct(p)}
                          className="hover:text-white ml-0.5"
                          aria-label={`Remove ${p}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {form.premium_products.length === 0 && (
                      <p className="text-white/30 text-xs italic">None yet</p>
                    )}
                  </div>
                </div>

                {/* Recommendations */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-1.5">
                    Site-specific recommendations
                    <span className="ml-2 text-white/30 text-xs font-normal">talking points, cross-sells, referral partners</span>
                  </label>
                  <textarea
                    value={form.recommendations}
                    onChange={e => setForm({ ...form, recommendations: e.target.value })}
                    rows={4}
                    placeholder="e.g. Suggest the on-site physio partner. Free 3-day trial on Elite. Group class Wednesdays at 6pm."
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                    data-testid="kbsc-recommendations"
                  />
                </div>

                {/* Email template */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-1.5">
                    Site-specific email template
                    <span className="ml-2 text-white/30 text-xs font-normal">used by follow-up emails after calls at this site</span>
                  </label>
                  <input
                    value={form.email_template.subject}
                    onChange={e => setForm({ ...form, email_template: { ...form.email_template, subject: e.target.value } })}
                    placeholder="Email subject line"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 mb-2 focus:outline-none focus:border-teal-500/50"
                    data-testid="kbsc-email-subject"
                  />
                  <textarea
                    value={form.email_template.body}
                    onChange={e => setForm({ ...form, email_template: { ...form.email_template, body: e.target.value } })}
                    rows={5}
                    placeholder="Hi {name}, thanks for calling {site} today. Here's a quick recap of what we discussed…"
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                    data-testid="kbsc-email-body"
                  />
                  <p className="text-white/30 text-[11px] mt-1 italic">
                    Placeholders: {'{name}'}, {'{site}'}, {'{agent}'}, {'{price_guide}'}
                  </p>
                </div>

                {/* Save */}
                <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
                  {configExistsForSite && (
                    <p className="text-white/40 text-xs mr-auto italic">
                      Override active — grading uses these values instead of the base KB
                    </p>
                  )}
                  <button
                    onClick={save}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                    data-testid="kbsc-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save site override
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminKBSiteConfigPage;
